import { enumValues, isEnumValue } from '../enum-utils.js';
import { ApBillStatus, ApBillType } from '../generated/enums.js';
import type { PermissionKey } from '../permissions.js';

/**
 * Bill lifecycle — the single module that owns AP bill status transitions and
 * which user actions each status allows.
 *
 * Transitions are the server state-machine outer limit. User actions are
 * narrower: a status may be transitionable to VOID on the server while the UI
 * still refuses a Void control (REJECTED), and a bill may be editable without
 * being voidable.
 *
 * Posted Void (APPROVED / PARTIALLY_PAID / PAID / COMPLETED) is a user action
 * only for a master approver. The server reverses GL entries in the same step
 * (`GL_REVERSAL_VOID`). PAID is treated as already posted (settlement happened),
 * so it does not also require `hasGlPostings`. REJECTED never gets Void in the UI.
 *
 * Payment PATCH after approval is narrower than bill-level UPDATE: a non-master
 * on APPROVED may only touch SCHEDULED/PENDING rows and
 * `APPROVED_BILL_UPDATABLE_FIELDS` (amount stays locked). A master approver on
 * APPROVED / PARTIALLY_PAID / PAID may edit any payment except RECONCILED, but
 * only `MASTER_APPROVER_UPDATABLE_FIELDS` (amount still locked). Pre-approval
 * and REJECTED return `allowedFields: null` (all fields). Ask
 * `getBillPaymentEditability` — do not copy these sets into screens.
 *
 * Do not copy these lists into screens or services. Ask this module.
 */

// Valid status transitions aligned with AP bills state machine:
// DRAFT → SUBMITTED → PENDING_APPROVAL → APPROVED → (PARTIALLY_PAID | PAID) (GL posting does not change status)
// A pending bill can return to SUBMITTED when its open approval chain is cancelled for review.
// On the AI posting path, final approval lands in PROCESSING_APPROVAL; it moves to APPROVED only
// together with its provision, or back to SUBMITTED when the ledger refuses both journals.
// APPROVED can revert to SUBMITTED (re-submit) or PENDING_APPROVAL when regressing with GL reversal.
// PAID → COMPLETED (when both GL postings exist — provision + payment clearing)
// PAID and PARTIALLY_PAID can be reversed via a GL reversal (master-approver only).
// BANK_FEE: DRAFT → COMPLETED is allowed only via validateStatusTransition(..., billType) (reconciliation shortcut).
// COMPLETED can only be VOIDed (master approver via edit-and-repost flow).
// REJECTED represents a bill sent back during approval and can return to DRAFT or be VOIDed.
// VOID is the only true terminal state.
// MIGRATED is outside the lifecycle: a bill imported from the customer's previous system
// (CSV `asMigrated` or the historical backfill). Nothing transitions INTO it — it is written
// only at creation — and nothing transitions OUT of it yet; an "activate" action is a
// follow-up. Until then a migrated bill is read-only from the status machine's point of view.
export const VALID_TRANSITIONS: Record<ApBillStatus, ApBillStatus[]> = {
  [ApBillStatus.DRAFT]: [
    ApBillStatus.SUBMITTED,
    ApBillStatus.PENDING_APPROVAL,
    ApBillStatus.REJECTED,
    ApBillStatus.VOID,
  ],
  [ApBillStatus.SUBMITTED]: [
    ApBillStatus.PENDING_APPROVAL,
    ApBillStatus.REJECTED,
    ApBillStatus.VOID,
  ],
  [ApBillStatus.PENDING_APPROVAL]: [
    ApBillStatus.APPROVED,
    ApBillStatus.PROCESSING_APPROVAL,
    ApBillStatus.REJECTED,
    ApBillStatus.SUBMITTED,
    ApBillStatus.VOID,
  ],
  [ApBillStatus.PROCESSING_APPROVAL]: [ApBillStatus.APPROVED, ApBillStatus.SUBMITTED],
  [ApBillStatus.APPROVED]: [
    ApBillStatus.PAID,
    ApBillStatus.PARTIALLY_PAID,
    ApBillStatus.SUBMITTED,
    ApBillStatus.PENDING_APPROVAL,
    ApBillStatus.REJECTED,
    ApBillStatus.VOID,
  ],
  [ApBillStatus.PARTIALLY_PAID]: [
    ApBillStatus.PAID,
    ApBillStatus.SUBMITTED,
    ApBillStatus.PENDING_APPROVAL,
    ApBillStatus.VOID,
  ],
  [ApBillStatus.PAID]: [
    ApBillStatus.COMPLETED,
    ApBillStatus.SUBMITTED,
    ApBillStatus.PENDING_APPROVAL,
    ApBillStatus.VOID,
  ],
  [ApBillStatus.COMPLETED]: [ApBillStatus.VOID],
  [ApBillStatus.REJECTED]: [ApBillStatus.DRAFT, ApBillStatus.PENDING_APPROVAL, ApBillStatus.VOID],
  [ApBillStatus.VOID]: [],
  [ApBillStatus.MIGRATED]: [],
};

export function validateStatusTransition(
  currentStatus: ApBillStatus,
  targetStatus: ApBillStatus,
  billType?: string | null,
): boolean {
  if (
    (billType === ApBillType.BANK_FEE || billType === ApBillType.VENDOR_ADVANCEMENT) &&
    currentStatus === ApBillStatus.DRAFT &&
    targetStatus === ApBillStatus.COMPLETED
  ) {
    return true;
  }
  const allowed = VALID_TRANSITIONS[currentStatus] ?? [];

  return allowed.includes(targetStatus);
}

export function getValidNextStatuses(currentStatus: ApBillStatus): ApBillStatus[] {
  return VALID_TRANSITIONS[currentStatus] ?? [];
}

/** User-facing bill actions owned by this module (not one function per action). */
export const BillUserAction = {
  VIEW: 'VIEW',
  EDIT: 'EDIT',
  DELETE: 'DELETE',
  SEND_FOR_APPROVAL: 'SEND_FOR_APPROVAL',
  VOID: 'VOID',
} as const;
export type BillUserAction = (typeof BillUserAction)[keyof typeof BillUserAction];

export const BILL_USER_ACTIONS = [
  BillUserAction.VIEW,
  BillUserAction.EDIT,
  BillUserAction.DELETE,
  BillUserAction.SEND_FOR_APPROVAL,
  BillUserAction.VOID,
] as const satisfies readonly BillUserAction[];

/**
 * Permission that gates each user action. Matches `@RequirePermission` on
 * `BillController`: VOID and DELETE use `ap:bill:delete`; send uses `ap:bill:submit`.
 */
export const BILL_ACTION_PERMISSION = {
  [BillUserAction.VIEW]: 'ap:bill:read',
  [BillUserAction.EDIT]: 'ap:bill:update',
  [BillUserAction.DELETE]: 'ap:bill:delete',
  [BillUserAction.SEND_FOR_APPROVAL]: 'ap:bill:submit',
  [BillUserAction.VOID]: 'ap:bill:delete',
} as const satisfies Record<BillUserAction, PermissionKey>;

/** Soft-delete is allowed only before approval (never on MIGRATED / VOID / posted). */
export const DELETABLE_BILL_STATUSES = [
  ApBillStatus.DRAFT,
  ApBillStatus.SUBMITTED,
  ApBillStatus.PENDING_APPROVAL,
  ApBillStatus.REJECTED,
] as const satisfies readonly ApBillStatus[];

/** Header fields, line items, withholdings, and list "Editar" share this set. */
export const EDITABLE_BILL_STATUSES = [
  ApBillStatus.DRAFT,
  ApBillStatus.SUBMITTED,
  ApBillStatus.PENDING_APPROVAL,
  ApBillStatus.REJECTED,
] as const satisfies readonly ApBillStatus[];

/**
 * Pre-approval Void offered by the detail header and bulk-void.
 * Narrower than the server map (which also allows REJECTED → VOID and posted-GL reversal).
 * Posted Void is answered by `canPerformBillAction` with `isMasterApprover` / `hasGlPostings`.
 */
export const VOIDABLE_BILL_STATUSES = [
  ApBillStatus.DRAFT,
  ApBillStatus.SUBMITTED,
  ApBillStatus.PENDING_APPROVAL,
] as const satisfies readonly ApBillStatus[];

/**
 * Posted bills that may offer Void / edit-and-repost (GL reversal) to a master approver.
 * A posted Void triggers a GL reversal server-side.
 */
export const POSTED_GL_REVERSAL_BILL_STATUSES = [
  ApBillStatus.APPROVED,
  ApBillStatus.PARTIALLY_PAID,
  ApBillStatus.PAID,
  ApBillStatus.COMPLETED,
] as const satisfies readonly ApBillStatus[];

/** Detail-page "Enviar para aprovação" — includes REJECTED (an approver sent it back). */
export const SEND_FOR_APPROVAL_BILL_STATUSES = [
  ApBillStatus.DRAFT,
  ApBillStatus.SUBMITTED,
  ApBillStatus.REJECTED,
] as const satisfies readonly ApBillStatus[];

/** Row checkboxes on `/ap/bills` for bulk send / void / merge / classify. */
export const BULK_ACTION_SELECTABLE_BILL_STATUSES = [
  ApBillStatus.DRAFT,
  ApBillStatus.SUBMITTED,
  ApBillStatus.PENDING_APPROVAL,
] as const satisfies readonly ApBillStatus[];

/** Posted bills that can have an open accounting-review request (includes COMPLETED). */
export const BILL_STATUSES_WITH_PENDING_REVIEW_REQUESTS = [
  ApBillStatus.APPROVED,
  ApBillStatus.PARTIALLY_PAID,
  ApBillStatus.PAID,
  ApBillStatus.COMPLETED,
] as const satisfies readonly ApBillStatus[];

/** Terminal or onboarding-history: view only. */
export const READ_ONLY_BILL_STATUSES = [
  ApBillStatus.VOID,
  ApBillStatus.MIGRATED,
] as const satisfies readonly ApBillStatus[];

/** Payment create / delete: same statuses as bill edit (never posted / migrated / void). */
export const BILL_PAYMENT_CREATE_STATUSES = EDITABLE_BILL_STATUSES;
export const BILL_PAYMENT_DELETE_STATUSES = EDITABLE_BILL_STATUSES;

/** Payment update: pre-approval plus APPROVED (operational / non-financial patches). */
export const BILL_PAYMENT_UPDATE_STATUSES = [
  ...EDITABLE_BILL_STATUSES,
  ApBillStatus.APPROVED,
] as const satisfies readonly ApBillStatus[];

export const BillPaymentAction = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
} as const;
export type BillPaymentAction = (typeof BillPaymentAction)[keyof typeof BillPaymentAction];

/** When bill is APPROVED, only these payment fields may be patched (non-financial / operational). */
export const APPROVED_BILL_UPDATABLE_FIELDS = [
  'notes',
  'originBankAccountId',
  'creditCardId',
  'initialStatus',
  'paidAmount',
  'paidDate',
] as const satisfies readonly string[];

export const MASTER_APPROVER_UPDATABLE_BILL_STATUSES = [
  ApBillStatus.DRAFT,
  ApBillStatus.SUBMITTED,
  ApBillStatus.PENDING_APPROVAL,
  ApBillStatus.APPROVED,
  ApBillStatus.PARTIALLY_PAID,
  ApBillStatus.PAID,
] as const satisfies readonly ApBillStatus[];

/**
 * Master-approver payment PATCH after approval. Amount (`scheduledAmount`) stays locked.
 */
export const MASTER_APPROVER_UPDATABLE_FIELDS = [
  'paymentMethod',
  'bankAccountId',
  'partyBankAccountId',
  'originBankAccountId',
  'creditCardId',
  'barcode',
  'boletoNossoNumero',
  'pixCopiaECola',
  'pixKeyType',
  'pixByBankData',
  'notes',
  'initialStatus',
  'paidAmount',
  'paidDate',
  'ccInstallmentCount',
  'scheduledDate',
] as const satisfies readonly string[];

export const MASTER_APPROVER_BLOCKED_PAYMENT_STATUSES = [
  'RECONCILED',
] as const satisfies readonly string[];

/**
 * Payments that commit money against the bill's net: scheduled, sent to the bank, paid or
 * reconciled. FAILED, CANCELLED and REVERSED do not count.
 */
export const BILL_PAYMENT_COMMITTED_STATUSES = [
  'SCHEDULED',
  'PENDING',
  'PROCESSING',
  'PAID',
  'RECONCILED',
] as const satisfies readonly string[];

/** When bill is APPROVED, a non-master may only update payments in this set. */
export const APPROVED_BILL_EDITABLE_PAYMENT_STATUSES = [
  'SCHEDULED',
  'PENDING',
] as const satisfies readonly string[];

export type ApprovedBillUpdatableField = (typeof APPROVED_BILL_UPDATABLE_FIELDS)[number];
export type MasterApproverUpdatablePaymentField = (typeof MASTER_APPROVER_UPDATABLE_FIELDS)[number];

export interface BillPaymentEditContext {
  billStatus: string;
  paymentStatus: string;
  isMasterApprover?: boolean;
}

export interface BillPaymentEditability {
  canEdit: boolean;
  /**
   * Field names the client may change. `null` means every payment field
   * (pre-approval and REJECTED). Empty when the bill itself cannot be patched.
   */
  allowedFields: readonly string[] | null;
}

const PRE_APPROVAL_PAYMENT_EDIT_BILL_STATUSES: ReadonlySet<string> = new Set([
  ApBillStatus.DRAFT,
  ApBillStatus.SUBMITTED,
  ApBillStatus.PENDING_APPROVAL,
]);

/**
 * Row-menu open control. Presentation-only mapping over `getAllowedBillActions`.
 * `review` / `mark_paid` ("Ver conta") are not extra lifecycle capabilities.
 */
export const BillRowOpenAction = {
  EDIT: 'edit',
  REVIEW: 'review',
  VIEW_BILL: 'mark_paid',
  VIEW: 'view',
} as const;
export type BillRowOpenAction = (typeof BillRowOpenAction)[keyof typeof BillRowOpenAction];

/**
 * i18n key under the `bills` namespace (`billStatuses.*`).
 * Web screens must not keep a parallel status→label map.
 */
export const BILL_STATUS_I18N_KEY = {
  [ApBillStatus.DRAFT]: 'billStatuses.draft',
  [ApBillStatus.SUBMITTED]: 'billStatuses.submitted',
  [ApBillStatus.PENDING_APPROVAL]: 'billStatuses.pendingApproval',
  [ApBillStatus.PROCESSING_APPROVAL]: 'billStatuses.processingApproval',
  [ApBillStatus.APPROVED]: 'billStatuses.approved',
  [ApBillStatus.REJECTED]: 'billStatuses.rejected',
  [ApBillStatus.PARTIALLY_PAID]: 'billStatuses.partiallyPaid',
  [ApBillStatus.PAID]: 'billStatuses.paid',
  [ApBillStatus.COMPLETED]: 'billStatuses.completed',
  [ApBillStatus.VOID]: 'billStatuses.void',
  [ApBillStatus.MIGRATED]: 'billStatuses.migrated',
} as const satisfies Record<ApBillStatus, string>;

export const UNKNOWN_BILL_STATUS = 'UNKNOWN' as const;

const DELETABLE_SET: ReadonlySet<string> = new Set(DELETABLE_BILL_STATUSES);
const EDITABLE_SET: ReadonlySet<string> = new Set(EDITABLE_BILL_STATUSES);
const VOIDABLE_SET: ReadonlySet<string> = new Set(VOIDABLE_BILL_STATUSES);
const SEND_FOR_APPROVAL_SET: ReadonlySet<string> = new Set(SEND_FOR_APPROVAL_BILL_STATUSES);
const READ_ONLY_SET: ReadonlySet<string> = new Set(READ_ONLY_BILL_STATUSES);
const POSTED_GL_REVERSAL_SET: ReadonlySet<string> = new Set(POSTED_GL_REVERSAL_BILL_STATUSES);
const PAYMENT_CREATE_SET: ReadonlySet<string> = new Set(BILL_PAYMENT_CREATE_STATUSES);
const PAYMENT_UPDATE_SET: ReadonlySet<string> = new Set(BILL_PAYMENT_UPDATE_STATUSES);
const PAYMENT_DELETE_SET: ReadonlySet<string> = new Set(BILL_PAYMENT_DELETE_STATUSES);
const MASTER_APPROVER_PAYMENT_BILL_SET: ReadonlySet<string> = new Set(
  MASTER_APPROVER_UPDATABLE_BILL_STATUSES,
);
const MASTER_APPROVER_BLOCKED_PAYMENT_SET: ReadonlySet<string> = new Set(
  MASTER_APPROVER_BLOCKED_PAYMENT_STATUSES,
);
const APPROVED_BILL_EDITABLE_PAYMENT_SET: ReadonlySet<string> = new Set(
  APPROVED_BILL_EDITABLE_PAYMENT_STATUSES,
);

export function isApBillStatus(value: unknown): value is ApBillStatus {
  return isEnumValue(ApBillStatus, value);
}

/**
 * Map a wire/list status onto the lifecycle. Legacy `GL_POSTED` becomes APPROVED.
 * Unknown values are kept (uppercased) — they must not become DRAFT.
 */
export function normalizeBillStatus(raw: unknown): ApBillStatus | string {
  if (typeof raw !== 'string' || raw.trim() === '') {
    return UNKNOWN_BILL_STATUS;
  }
  const normalized = raw.trim().toUpperCase();
  if (normalized === 'GL_POSTED') {
    return ApBillStatus.APPROVED;
  }
  if (isApBillStatus(normalized)) {
    return normalized;
  }
  return normalized;
}

export function getBillStatusI18nKey(status: string): string | undefined {
  if (!isApBillStatus(status)) return undefined;
  return BILL_STATUS_I18N_KEY[status];
}

export interface BillActionContext {
  status: string;
  permissions: readonly string[];
  billType?: string | null;
  /** Master approver may void / edit-and-repost a posted bill (GL reversal). */
  isMasterApprover?: boolean;
  /** Bill already has GL postings. Required for posted Void except PAID. */
  hasGlPostings?: boolean;
}

function hasBillPermission(permissions: readonly string[], required: PermissionKey): boolean {
  return permissions.includes('*') || permissions.includes(required);
}

/**
 * Posted Void / edit-and-repost: master approver + GL postings.
 * PAID does not require `hasGlPostings` (settlement already posted).
 */
export function isPostedBillGlReversalAllowed(
  context: Pick<BillActionContext, 'status' | 'isMasterApprover' | 'hasGlPostings'>,
): boolean {
  if (!context.isMasterApprover || !isApBillStatus(context.status)) {
    return false;
  }
  if (!POSTED_GL_REVERSAL_SET.has(context.status)) {
    return false;
  }
  if (context.status === ApBillStatus.PAID) {
    return true;
  }
  return Boolean(context.hasGlPostings);
}

function statusAllowsVoid(
  context: Pick<BillActionContext, 'status' | 'isMasterApprover' | 'hasGlPostings'>,
): boolean {
  if (!isApBillStatus(context.status) || READ_ONLY_SET.has(context.status)) {
    return false;
  }
  if (VOIDABLE_SET.has(context.status)) {
    return true;
  }
  return isPostedBillGlReversalAllowed(context);
}

function statusAllowsAction(
  context: Pick<BillActionContext, 'status' | 'isMasterApprover' | 'hasGlPostings'>,
  action: BillUserAction,
): boolean {
  const { status } = context;
  if (!isApBillStatus(status)) {
    return action === BillUserAction.VIEW;
  }
  if (READ_ONLY_SET.has(status) && action !== BillUserAction.VIEW) {
    return false;
  }
  switch (action) {
    case BillUserAction.VIEW:
      return true;
    case BillUserAction.EDIT:
      return EDITABLE_SET.has(status);
    case BillUserAction.DELETE:
      return DELETABLE_SET.has(status);
    case BillUserAction.SEND_FOR_APPROVAL:
      return SEND_FOR_APPROVAL_SET.has(status);
    case BillUserAction.VOID:
      return statusAllowsVoid(context);
  }
}

/** Status-only rule (ignore permissions). Posted Void without master/GL context is false. */
export function isBillActionAllowedForStatus(status: string, action: BillUserAction): boolean {
  return statusAllowsAction({ status }, action);
}

/**
 * Can this user do action X to this bill?
 * Answers from status (and bill type / posted-GL flags where they matter) plus permission codes.
 * `'*'` is treated as all permissions, matching `packages/types/src/permissions.ts`.
 */
export function canPerformBillAction(
  context: BillActionContext & { action: BillUserAction },
): boolean {
  if (!statusAllowsAction(context, context.action)) {
    return false;
  }
  return hasBillPermission(context.permissions, BILL_ACTION_PERMISSION[context.action]);
}

export function isBillPaymentActionAllowed(status: string, action: BillPaymentAction): boolean {
  if (!isApBillStatus(status)) {
    return false;
  }
  switch (action) {
    case BillPaymentAction.CREATE:
      return PAYMENT_CREATE_SET.has(status);
    case BillPaymentAction.UPDATE:
      return PAYMENT_UPDATE_SET.has(status);
    case BillPaymentAction.DELETE:
      return PAYMENT_DELETE_SET.has(status);
  }
}

/**
 * Per-payment PATCH rules after the bill-level create/update/delete lists.
 * Amount is never in the approved / master field sets (locked after approval).
 */
export function getBillPaymentEditability(context: BillPaymentEditContext): BillPaymentEditability {
  const { billStatus, paymentStatus, isMasterApprover = false } = context;

  if (isMasterApprover) {
    if (!MASTER_APPROVER_PAYMENT_BILL_SET.has(billStatus)) {
      return { canEdit: false, allowedFields: null };
    }
    if (!PRE_APPROVAL_PAYMENT_EDIT_BILL_STATUSES.has(billStatus)) {
      return {
        canEdit: !MASTER_APPROVER_BLOCKED_PAYMENT_SET.has(paymentStatus),
        allowedFields: MASTER_APPROVER_UPDATABLE_FIELDS,
      };
    }
    return { canEdit: true, allowedFields: null };
  }

  if (!PAYMENT_UPDATE_SET.has(billStatus)) {
    return { canEdit: false, allowedFields: null };
  }
  if (billStatus === ApBillStatus.APPROVED) {
    return {
      canEdit: APPROVED_BILL_EDITABLE_PAYMENT_SET.has(paymentStatus),
      allowedFields: APPROVED_BILL_UPDATABLE_FIELDS,
    };
  }
  return { canEdit: true, allowedFields: null };
}

/** Row-menu open control for a known status; unknown statuses return null (no buttons). */
export function getBillRowOpenAction(status: string): BillRowOpenAction | null {
  if (!isApBillStatus(status)) {
    return null;
  }
  switch (status) {
    case ApBillStatus.DRAFT:
    case ApBillStatus.REJECTED:
      return BillRowOpenAction.EDIT;
    case ApBillStatus.SUBMITTED:
    case ApBillStatus.PENDING_APPROVAL:
      return BillRowOpenAction.REVIEW;
    case ApBillStatus.APPROVED:
    case ApBillStatus.PARTIALLY_PAID:
      return BillRowOpenAction.VIEW_BILL;
    case ApBillStatus.PROCESSING_APPROVAL:
    case ApBillStatus.PAID:
    case ApBillStatus.COMPLETED:
    case ApBillStatus.VOID:
    case ApBillStatus.MIGRATED:
      return BillRowOpenAction.VIEW;
  }
}

/** Every user action this principal may take on the bill. */
export function getAllowedBillActions(context: BillActionContext): BillUserAction[] {
  return BILL_USER_ACTIONS.filter((action) => canPerformBillAction({ ...context, action }));
}

/** All known statuses — useful for exhaustive UI maps. */
export const ALL_BILL_STATUSES: readonly ApBillStatus[] = enumValues(ApBillStatus);
